<script setup lang="ts">
import { capitalize, computed, defineEmits, defineProps, ref } from "vue";
import { useI18n } from "vue-i18n";
import DatePicker from "@/components/inputs/DatePicker.vue";
import AppSelect from "@/components/inputs/AppSelect.vue";
import GooglePlaceAutocompleteInput from "@/components/inputs/GooglePlaceAutocompleteInput.vue";
import { PlaceSelectedPayload } from "@/types/google-places";
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";
import { MONTHS_KEYS } from "@/constants/chineseCalendar";
import CheckBox from "@/components/inputs/CheckBox.vue";
import GradientButton from "@/components/buttons/GradientButton.vue";
import { FavorableDateSelectionRequest } from "@/types/calculators";
import { formatLocalDate } from "@/utils/date";

defineProps<{
  showFullCard: boolean;
}>();

const mountains = [
  "null",
  "子",
  "癸",
  "丑",
  "艮",
  "寅",
  "甲",
  "卯",
  "乙",
  "辰",
  "巽",
  "巳",
  "丙",
  "午",
  "丁",
  "未",
  "坤",
  "申",
  "庚",
  "酉",
  "辛",
  "戌",
  "乾",
  "亥",
  "壬",
];
const calculators = ["tong_shu_60", "civilian_37", "imperial_67"];

const { t } = useI18n();
const { normalizeGoogleTimeZone } = useGoogleAutocomplete("ru");
const emit = defineEmits<{
  (e: "submitForm", request: FavorableDateSelectionRequest): void;
}>();

// Form Params
const selectedCalculator = ref(calculators[0]);
const selectedMountain = ref(mountains[0]);
const latitude = ref<number | null>(null);
const longitude = ref<number | null>(null);
const timeZone = ref<string | null>(null);
const selectedMonth = ref<number>(new Date().getMonth());
const selectedYear = ref<number>(new Date().getFullYear());
const birthDate = ref<Date | null>(null);
const birthTime = ref<string | null>(null);
const isMale = ref<boolean>(true);
const usefulElements = ref({
  wood: false,
  fire: false,
  earth: false,
  metal: false,
  water: false,
});
const useTrueSolarTime = ref(false);

// Form Settings
const isLoading = ref(false);
const supportedYears = computed(() => {
  const yearsCount = 100;
  const startYear = selectedYear.value - yearsCount / 2;

  return Array.from({ length: yearsCount + 1 }, (_, i) => startYear + i);
});
const isFormValid = computed(() => {
  return (
    birthDate.value !== null &&
    birthTime.value !== null &&
    latitude.value !== null &&
    longitude.value !== null &&
    timeZone.value !== null
  );
});
const periodString = computed(() => {
  const year = selectedYear.value;
  const month = selectedMonth.value;

  // 1. Форматируем месяц, чтобы всегда было две цифры (например, '10' вместо '9')
  // В JS месяцы идут от 0 до 11, поэтому добавляем 1
  const formattedMonth = String(month + 1).padStart(2, "0");

  // 2. Находим последний день выбранного месяца
  // Передача 0 в качестве дня для следующего месяца возвращает последний день текущего
  const lastDay = new Date(year, month + 1, 0).getDate();
  const formattedLastDay = String(lastDay).padStart(2, "0");

  // 3. Собираем итоговую строку
  return `01.${formattedMonth}.${year} – ${formattedLastDay}.${formattedMonth}.${year}`;
});

function handlePlaceSelected(payload: PlaceSelectedPayload) {
  latitude.value = payload.location.lat();
  longitude.value = payload.location.lng();
  timeZone.value = normalizeGoogleTimeZone(payload.timeZone);
}

function handleToggleUsefulElement(
  elementKey: keyof typeof usefulElements.value
) {
  usefulElements.value[elementKey] = !usefulElements.value[elementKey];
}

function handleSubmit() {
  const formattedMonth = String(selectedMonth.value + 1).padStart(2, "0");

  emit("submitForm", {
    requested_date: `${selectedYear.value}-${formattedMonth}-01`,
    birthday: formatLocalDate(birthDate.value as Date),
    is_male: isMale.value ? 1 : 0,
    latitude: latitude.value as number,
    longitude: longitude.value as number,
    timezone: timeZone.value as string,
    activity_scope: selectedCalculator.value,
    ...(selectedMountain.value !== "null" && {
      house_facing: selectedMountain.value,
    }),
    favourable_elements: Object.keys(usefulElements.value)
      .filter(
        (key) => usefulElements.value[key as keyof typeof usefulElements.value]
      )
      .map(capitalize),
    use_true_solar_time: useTrueSolarTime.value ? 1 : 0,
  });
}
</script>

<template>
  <div
    class="bg-white rounded-2xl sm:rounded-3xl border border-divider p-4 sm:p-7 shadow-sm space-y-6 text-left"
  >
    <template v-if="!showFullCard">
      <div class="flex gap-2">
        <span class="text-muted">Період:</span>
        <strong class="text-brown">{{ periodString }}</strong>
      </div>
    </template>
    <template v-else>
      <div class="flex justify-between border-b border-divider pb-4">
        <div>
          <h2 class="text-base sm:text-lg font-bold text-brown">
            {{ t("calculators.personal-date.settings-card.title") }}
          </h2>
          <p class="text-xs text-muted mt-0.5">
            {{ t("calculators.personal-date.settings-card.desc") }}
          </p>
        </div>
      </div>
      <!---->
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
      >
        <div>
          <label class="block text-xs sm:text-sm font-bold text-brown mb-3">
            {{ t("labels.build-year") }}
          </label>
          <AppSelect
            class="w-full"
            :items="supportedYears"
            :default-value="selectedYear"
            @select-option="(newSelectedYear: number) => selectedYear = Number(newSelectedYear)"
          />
        </div>
        <div>
          <label class="block text-xs sm:text-sm font-bold text-brown mb-3">
            {{ t("labels.build-month") }}
          </label>
          <AppSelect
            class="w-full"
            :items="MONTHS_KEYS"
            :default-value="MONTHS_KEYS[selectedMonth]"
            @select-option="(newSelectedMonth: string) => selectedMonth = MONTHS_KEYS.indexOf(newSelectedMonth)"
          >
            <template #option="{ item }">
              {{ t(`months.${item}`) }}
            </template>
          </AppSelect>
        </div>
        <div>
          <label class="block text-xs sm:text-sm font-bold text-brown mb-2">
            {{ t("labels.birthday") }}
          </label>
          <DatePicker
            id="dateFrom"
            @dateSelected="(newDate: Date) => birthDate = newDate"
          />
        </div>
        <div class="grid grid-cols-2 gap-2.5">
          <div>
            <label class="block text-xs sm:text-sm font-bold text-brown mb-2">
              {{ t("labels.birthtime") }}
            </label>
            <input
              id="time"
              v-model="birthTime"
              type="time"
              class="border border-divider pl-4 pr-[6px] w-full h-[52px] bg-secondary rounded-[14px] sf-pro font-normal text-base text-muted"
            />
          </div>
          <div>
            <label class="block text-xs sm:text-sm font-bold text-brown mb-2">
              {{ t("calculators.personal-date.settings-card.gender.title") }}
            </label>
            <div class="grid grid-cols-2 bg-secondary p-1 rounded-xl h-[52px]">
              <button
                type="button"
                class="font-semibold shadow-sm rounded-lg text-xs transition-all"
                :class="
                  isMale ? 'bg-brown text-white' : 'text-muted hover:text-brown'
                "
                @click="isMale = true"
              >
                {{ t("calculators.personal-date.settings-card.gender.male") }}
              </button>
              <button
                type="button"
                class="rounded-lg text-xs transition-all"
                :class="
                  isMale ? 'text-muted hover:text-brown' : 'bg-brown text-white'
                "
                @click="isMale = false"
              >
                {{ t("calculators.personal-date.settings-card.gender.female") }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 pt-2">
        <div>
          <label class="block text-xs font-bold text-brown mb-2">
            {{
              t("calculators.personal-date.settings-card.true-solar-location")
            }}
          </label>
          <GooglePlaceAutocompleteInput
            styleType="outline"
            @placeSelected="handlePlaceSelected"
          />
        </div>
        <div class="grid grid-cols-2 gap-2.5">
          <div>
            <label class="block text-xs font-bold text-brown mb-3">
              {{ t("calculators.personal-date.settings-card.case-catalog") }}
            </label>
            <AppSelect
              :items="calculators"
              :defaultValue="selectedCalculator"
              class="w-full"
              @select-option="(newCalculator: string) => selectedCalculator = newCalculator"
            >
              <template #option="{ item }">
                {{ t(`calculators.personal-date.settings-card.calc.${item}`) }}
              </template>
            </AppSelect>
          </div>
          <div>
            <label class="block text-xs font-bold text-brown mb-3">
              {{ t("calculators.personal-date.settings-card.house-facade") }}
            </label>
            <AppSelect
              :items="mountains"
              :defaultValue="selectedMountain"
              class="w-full"
            >
              <template #option="{ item }">
                {{
                  item === "null"
                    ? t(
                        "calculators.personal-date.settings-card.unknown-mountain"
                      )
                    : t("calculators.personal-date.settings-card.mountain", {
                        mountain: item,
                      })
                }}
              </template>
            </AppSelect>
          </div>
        </div>
        <div>
          <label class="block text-xs font-bold text-brown mb-2">
            {{ t("calculators.personal-date.settings-card.useful-elements") }}
          </label>
          <div
            class="grid grid-cols-[repeat(auto-fill,minmax(115px,1fr))] gap-x-4 gap-y-2.5 pt-1"
          >
            <CheckBox
              input-id="wood"
              :label="
                t('calculators.personal-date.settings-card.elements.wood')
              "
              @toggle="handleToggleUsefulElement('wood')"
              class="select-none !w-auto"
            />
            <CheckBox
              input-id="fire"
              :label="
                t('calculators.personal-date.settings-card.elements.fire')
              "
              @toggle="handleToggleUsefulElement('fire')"
              class="select-none !w-auto"
            />
            <CheckBox
              input-id="earth"
              :label="
                t('calculators.personal-date.settings-card.elements.earth')
              "
              @toggle="handleToggleUsefulElement('earth')"
              class="select-none !w-auto"
            />
            <CheckBox
              input-id="metal"
              :label="
                t('calculators.personal-date.settings-card.elements.metal')
              "
              @toggle="handleToggleUsefulElement('metal')"
              class="select-none !w-auto"
            />
            <CheckBox
              input-id="water"
              :label="
                t('calculators.personal-date.settings-card.elements.water')
              "
              @toggle="handleToggleUsefulElement('water')"
              class="select-none !w-auto"
            />
          </div>
        </div>
      </div>
      <div
        class="pt-5 border-t border-divider flex flex-col sm:flex-row items-center justify-between gap-4"
      >
        <CheckBox
          input-id="true_solar_time"
          :label="
            t('calculators.personal-date.settings-card.use-true-solar-time')
          "
          @toggle="useTrueSolarTime = !useTrueSolarTime"
          class="select-none !w-auto"
        />
        <GradientButton
          class="w-[153px] h-[46px] md:w-[191px] md:h-14"
          bg-color="bg-brown-gradient"
          hover-bg-color="bg-orange-gradient"
          :text="t('buttons.calculate')"
          @click="handleSubmit"
          :is-loading="isLoading"
          :isDisabled="!isFormValid"
        />
      </div>
    </template>
  </div>
</template>

<style scoped></style>
